import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { InferenceAPI, useTokenAndOrgId } from './utils'
import {
  getServerViewSort,
  ServerViewSortKeys,
} from '@/pages/DashboardServers/types'
import { processPaginatedData } from './utils'
import {
  CreateServerInstanceRequest,
  UpdateServerInstanceArgs,
  UpdateServerInstanceScaleConfigArgs,
  GetServerWebSSHSessionArgs,
} from './inference.gen'
import toast from 'react-hot-toast'

const LIST_SERVER_PAGE_SIZE = 10

export const useListServerInstances = ({
  showAllUsers = false,
  sort = undefined,
  searchText = '',
  refetchInterval,
  staleTime,
}: {
  showAllUsers?: boolean
  sort?: ServerViewSortKeys
  searchText?: string
  refetchInterval?: number
  staleTime?: number
}) => {
  const { orgId, token } = useTokenAndOrgId()

  const sortData = sort
    ? getServerViewSort(sort)
    : getServerViewSort(ServerViewSortKeys.STARTED_AT_DESC)

  const fetchServerInstances = async ({ pageParam = 1 }) => {
    const body = {
      org_id: orgId,
      page_number: pageParam,
      page_size: LIST_SERVER_PAGE_SIZE,
      show_all_users: showAllUsers,
      sort_direction: sortData.sortDirection,
      sort_by: sortData.sortBy,
      search_text: searchText,
    }

    const result = await InferenceAPI.listServerInstances({
      request: body,
    })

    return result
  }

  return useInfiniteQuery({
    queryKey: [
      'list-server-instances',
      orgId,
      token,
      showAllUsers,
      sort,
      searchText,
    ],
    queryFn: fetchServerInstances,
    enabled: !!orgId && !!token,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage || !lastPage.instances) {
        return undefined
      }
      return lastPage.instances.length === LIST_SERVER_PAGE_SIZE
        ? pages.length + 1
        : undefined
    },
    // Use processPaginatedData for consistent and reusable logic
    select: (data) => processPaginatedData(data.pages),
    refetchInterval,
    staleTime,
  })
}

interface UseGetServerInstanceOptions {
  enabled?: boolean
  refetchInterval?: number | false
}

export const useGetServerInstance = (
  instanceId: number,
  options?: UseGetServerInstanceOptions
) => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['get-server-instance', instanceId, orgId, token],
    enabled: (options?.enabled ?? true) && !!orgId && !!token && !!instanceId,
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const result = await InferenceAPI.getServerInstance({
        instance_id: instanceId,
      })
      return result.instance
    },
  })
}

export const useCreateServerInstance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: CreateServerInstanceRequest) => {
      const result = await InferenceAPI.createServerInstance({
        request: {
          ...args,
          storage_volume_id: args.storage_volume_id || undefined,
          storage_volume_ids: args.storage_volume_ids || undefined,
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['list-server-instances'],
        exact: false,
      })

      return result.instance_id
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}

export const useDeleteServerInstance = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (instanceId: number) => {
      const result = await InferenceAPI.deleteServerInstance({
        instance_id: instanceId,
      })

      queryClient.invalidateQueries({
        queryKey: ['list-server-instances'],
        exact: false,
      })

      return result.ok
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}
export const useUpdateServerInstance = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: UpdateServerInstanceArgs) => {
      const result = await InferenceAPI.updateServerInstance(args)

      queryClient.invalidateQueries({
        queryKey: ['list-server-instances'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['get-server-instance', args.instance_id, orgId, token],
        exact: false,
      })

      return result.ok
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}

export const useUpdateServerInstanceScaleConfig = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: UpdateServerInstanceScaleConfigArgs) => {
      const result = await InferenceAPI.updateServerInstanceScaleConfig(args)

      queryClient.invalidateQueries({
        queryKey: ['list-server-instances'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['get-server-instance'],
        exact: false,
      })

      return result.ok
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}

function toGiB(bytes: number) {
  return bytes / 1024 ** 3
}

export function useGetServerInstanceMetrics(
  instanceId: number,
  orgId: number,
  token?: string
) {
  // refetchInterval set to 10s
  const result = useQuery({
    queryKey: ['serverInstanceMetrics', instanceId],
    queryFn: async () => {
      if (!token) {
        throw new Error('No auth token provided')
      }
      const result = await InferenceAPI.getServerInstanceMetrics({
        instance_id: instanceId,
      })

      return result
    },
    enabled: Boolean(instanceId && orgId && token),
    refetchInterval: 10000,
  })

  let cpuUsage = 0
  let cpuLimit = 0
  let memoryUsage = 0
  let memoryLimit = 0
  let cpuPercent = 0
  let memoryPercent = 0
  let memoryUsageGiB = 0
  let memoryLimitGiB = 0

  if (result.data) {
    const { cpu: CPU, memory: Memory } = result.data.metrics[0] // TODO: handle multiple metrics
    cpuUsage = CPU.usage
    cpuLimit = CPU.limit
    memoryUsage = Memory.usage
    memoryLimit = Memory.limit

    cpuPercent = cpuLimit > 0 ? (cpuUsage / cpuLimit) * 100 : 0
    memoryPercent = memoryLimit > 0 ? (memoryUsage / memoryLimit) * 100 : 0

    memoryUsageGiB = toGiB(memoryUsage)
    memoryLimitGiB = toGiB(memoryLimit)
  }

  return {
    ...result,
    cpuUsage,
    cpuLimit,
    memoryUsage,
    memoryLimit,
    cpuPercent,
    memoryPercent,
    memoryUsageGiB,
    memoryLimitGiB,
  }
}

export const useGetServerInstancePodName = (instanceId: number) => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['get-server-instance-pod-name', instanceId, orgId, token],
    enabled: !!orgId && !!token && !!instanceId,
    queryFn: async () => {
      const result = await InferenceAPI.getServerInstancePodName({
        instance_id: instanceId,
      })
      return result.pod_name
    },
    refetchInterval: 30000,
  })
}

type UseGetServerInstanceScaleHistoryOptions = {
  instanceId: number
  startAt: number
  endAt: number
  unit: 'second' | 'minute' | 'hour'
  enabled?: boolean
}

export const useGetServerInstanceScaleHistory = ({
  instanceId,
  startAt,
  endAt,
  unit,
  enabled = true,
}: UseGetServerInstanceScaleHistoryOptions) => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: [
      'get-server-instance-scale-history',
      instanceId,
      startAt,
      endAt,
      unit,
      orgId,
      token,
    ],
    enabled: enabled && !!orgId && !!token && !!instanceId,
    queryFn: async () => {
      const result = await InferenceAPI.getServerInstanceScaleHistory({
        instance_id: instanceId,
        start_at: startAt,
        end_at: endAt,
        unit: unit,
      })
      console.log('result', result)
      return result.items
    },
  })
}

export const useGetServerWebSSHSession = () => {
  return useMutation({
    mutationFn: async (args: GetServerWebSSHSessionArgs) => {
      const result = await InferenceAPI.getServerWebSSHSession(args)
      return result.url
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}

export const useMountStorage = () => {
  const queryClient = useQueryClient()
  const { orgId, token } = useTokenAndOrgId()

  return useMutation({
    mutationFn: async (args: { instanceId: number; volumeIds: number[] }) => {
      const result = await InferenceAPI.mountStorage({
        instance_id: args.instanceId,
        volume_ids: args.volumeIds,
      })
      return result
    },
    onSuccess: (_, args) => {
      // Invalidate the server instance query to refresh mounted volumes
      queryClient.invalidateQueries({
        queryKey: ['get-server-instance', args.instanceId, orgId, token],
        exact: false,
      })
      toast.success('Storage volumes mounted successfully')
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}

export const useSetAutoShutdown = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { instance_id: number; timestamp?: number }) => {
      const result = await InferenceAPI.setAutoShutdown({
        request: args,
      })

      queryClient.invalidateQueries({
        queryKey: ['list-server-instances'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['get-server-instance', args.instance_id, orgId, token],
        exact: false,
      })

      return result.ok
    },
    onError: (error) => {
      toast.error(JSON.stringify(error))
    },
  })
}
